import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { shouldExcludeFromActivityFeed, activityRowShouldExcludeFromFeed } from '../../utils/activityFeedExclude';
import { activityRowFingerprint } from '../../utils/activitySocketPayload';
import { normalizeActivityData } from '../../utils/activityPayload';
import { api } from '../api/axiosManager';
import { normalizeRatingsPagination } from '../../services/ratingService';

const STALE_TIMES = {
  dashboardStats: 60 * 1000,
  realtimeData: 15 * 1000, // 15 saniye - realtime veriler için kısa TTL
  campaignAnalytics: 5 * 60 * 1000, // ✅ 5 dakika (backend yavaş olduğu için cache süresini artırdık)
  ratings: 5 * 60 * 1000, // ✅ 5 dakika (backend yavaş olduğu için cache süresini artırdık)
  campaignCTR: 5 * 60 * 1000, // ✅ 5 dakika (backend yavaş olduğu için cache süresini artırdık)
};

const shouldSkipFetch = (lastFetched, ttl) => {
  if (!lastFetched) return false;
  return Date.now() - lastFetched < ttl;
};

const DEFAULT_CAMPAIGN_ANALYTICS = {
  activeUsers: 0,
  popularCampaigns: [],
  totalEvents: 0,
  totalCampaigns: 0,
};

// Yorumlar için varsayılan durum
const DEFAULT_RATINGS = {
  data: [],
  pagination: {},
};

function mergePopularCampaignsIntoState(state, syncData, kpiWithoutExtras) {
  const popularCampaigns = syncData.popularCampaigns || kpiWithoutExtras?.popularCampaigns;
  if (!Array.isArray(popularCampaigns) || popularCampaigns.length === 0) {
    return;
  }

  if (!state.campaignAnalytics) {
    state.campaignAnalytics = DEFAULT_CAMPAIGN_ANALYTICS;
  }

  const mevcutPopularCampaigns = state.campaignAnalytics.popularCampaigns || [];
  state.campaignAnalytics.popularCampaigns = popularCampaigns.map((backendCampaign) => {
    const mevcutCampaign = mevcutPopularCampaigns.find(
      (c) => c.campaignId === backendCampaign.campaignId
    );
    const campaignTitle =
      backendCampaign.campaignTitle
      || backendCampaign.campaignName
      || mevcutCampaign?.campaignTitle
      || mevcutCampaign?.campaignName
      || null;

    return {
      ...backendCampaign,
      campaignTitle,
      campaignName: campaignTitle,
    };
  });
}

function applyCampaignKpiFields(state, kpiWithoutExtras, campaignViews) {
  if (!state.dashboardStats) {
    state.dashboardStats = {};
  }
  if (!state.dashboardStats.kpi) {
    state.dashboardStats.kpi = {};
  }
  if (!state.campaignAnalytics) {
    state.campaignAnalytics = DEFAULT_CAMPAIGN_ANALYTICS;
  }

  if (kpiWithoutExtras?.screenViews !== undefined && kpiWithoutExtras.screenViews !== null) {
    state.dashboardStats.kpi.screenViews = kpiWithoutExtras.screenViews;
    state.screenViews = kpiWithoutExtras.screenViews;
  }
  if (campaignViews !== undefined && campaignViews !== null) {
    state.dashboardStats.kpi.campaignViews = campaignViews;
  }
  if (kpiWithoutExtras?.totalCampaigns !== undefined && kpiWithoutExtras.totalCampaigns !== null) {
    state.dashboardStats.kpi.totalCampaigns = kpiWithoutExtras.totalCampaigns;
    state.campaignAnalytics.totalCampaigns = kpiWithoutExtras.totalCampaigns;
  }
  if (kpiWithoutExtras?.totalEvents !== undefined && kpiWithoutExtras.totalEvents !== null) {
    state.dashboardStats.kpi.totalEvents = kpiWithoutExtras.totalEvents;
    state.campaignAnalytics.totalEvents = kpiWithoutExtras.totalEvents;
  }
}

function parseKpiPayload(rawKpi) {
  if (!rawKpi) return null;
  let cleanKpi = rawKpi;
  if (cleanKpi.success && cleanKpi.data) {
    cleanKpi = cleanKpi.data;
  }
  const { campaignViews, success, cacheTimestamp, fromCache, sections, ...kpiWithoutExtras } = cleanKpi;
  return { kpiWithoutExtras, campaignViews };
}

// KPI verilerini çek (Ana dashboard verileri)
// ✅ Endpoint: /api/admin/dashboard/kpi
// ✅ Unified format: success:true + data { kpi, charts, recentActivities, activeUsersNow, activeUsersInRange, userBehavior, platformDistribution, activitiesStats, timestamp }
// ❗️ Varsayılan format=unified
export const fetchCampaignAnalytics = createAsyncThunk(
  'dashboard/fetchCampaignAnalytics',
  async (options = {}, { rejectWithValue }) => {
    try {
      const { dateRange, force, updateActiveUsersNow = false, sections } = options;
      
      // Query parametrelerini oluştur
      const params = new URLSearchParams();
      if (dateRange) params.append('dateRange', dateRange);
      if (sections) params.append('sections', sections);
      // Varsayılan unified format
      if (!params.has('format')) params.append('format', 'unified');
      
      const url = `/api/admin/dashboard/kpi${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await api.get(url);
      
      // Unified format: { success:true, data:{ kpi, charts, ... } }
      if (response.data?.success && response.data?.data) {
        const syncScope = sections === 'core' ? 'core' : sections === 'campaigns' ? 'campaigns' : 'all';
        return {
          syncData: response.data.data, // dashboard:sync ile aynı format
          updateActiveUsersNow: updateActiveUsersNow === true,
          syncScope,
        };
      }

      return rejectWithValue('KPI unified format bekleniyordu');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error?.message || 'KPI verileri yüklenemedi');
    }
  },
  {
    condition: ({ force, sections } = {}, { getState }) => {
      if (force) return true;
      const dashboardState = getState().dashboard;
      const cacheKey = sections === 'campaigns' ? 'kpiCampaigns' : 'kpiCore';
      const lastFetched = dashboardState.lastFetched[cacheKey];

      // Kampanya listesi state'te yoksa cache'e bakmadan tekrar çek
      if (sections === 'campaigns') {
        const hasPopular = (dashboardState.campaignAnalytics?.popularCampaigns?.length ?? 0) > 0;
        if (!hasPopular) return true;
      }

      if (!lastFetched) return true;
      if (shouldSkipFetch(lastFetched, STALE_TIMES.campaignAnalytics)) {
        return false;
      }
      return true;
    },
  }
);

// Kampanya CTR analizini çek
export const fetchCampaignCTR = createAsyncThunk(
  'dashboard/fetchCampaignCTR',
  async (options = {}, { rejectWithValue }) => {
    try {
      const { dateRange, force } = options;
      
      // Query parametrelerini oluştur
      const params = new URLSearchParams();
      if (dateRange) params.append('dateRange', dateRange);
      
      // ✅ Yeni endpoint: /api/admin/analytics/campaign-ctr
      const url = `/api/admin/analytics/campaign-ctr${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await api.get(url);
      
      if (response.data && response.data.success && response.data.data) {
        const ctrData = response.data.data;
        return ctrData; // { general, personalized, total }
      }
      return {
        general: { impressions: 0, detailViews: 0, ctr: 0 },
        personalized: { impressions: 0, detailViews: 0, ctr: 0 },
        total: { impressions: 0, detailViews: 0, ctr: 0 },
      };
    } catch (error) {
      // Timeout hatası veya endpoint yoksa sessizce varsayılan değerler döndür
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isNotFoundError = error.response?.status === 404;
      
      if (isTimeoutError || isNotFoundError) {
        return {
          general: { impressions: 0, detailViews: 0, ctr: 0 },
          personalized: { impressions: 0, detailViews: 0, ctr: 0 },
          total: { impressions: 0, detailViews: 0, ctr: 0 },
        };
      }
      return rejectWithValue(error.response?.data?.message || 'Kampanya CTR analizi yüklenemedi');
    }
  },
  {
    condition: ({ force } = {}, { getState }) => {
      if (force) return true;
      const lastFetched = getState().dashboard.lastFetched.campaignCTR;
      if (shouldSkipFetch(lastFetched, STALE_TIMES.dashboardStats || 60 * 1000)) {
        return false;
      }
      return true;
    },
  }
);

// Dashboard yorumlarını çek
export const fetchRatings = createAsyncThunk(
  'dashboard/fetchRatings',
  async (options = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 20,
        dateRange,
        dateFrom,
        dateTo,
        force,
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (dateRange) params.append('dateRange', dateRange);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const url = `/api/admin/dashboard/ratings?${params.toString()}`;
      const response = await api.get(url);

      if (response.data && response.data.success) {
        const list = response.data.data || [];
        return {
          data: list,
          pagination: normalizeRatingsPagination(response.data.pagination || {}, {
            page,
            limit,
            dataLength: list.length,
          }),
        };
      }
      return rejectWithValue('Yorumlar yüklenemedi');
    } catch (error) {
      // Timeout ve network hatalarını özel olarak handle et
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return rejectWithValue('Yorumlar alınırken zaman aşımı oluştu. Lütfen daha sonra tekrar deneyin.');
      }
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        return rejectWithValue('Sunucuya erişilemiyor. Lütfen internet bağlantınızı kontrol edin.');
      }
      // HTTP hataları için
      if (error.response?.status >= 500) {
        return rejectWithValue(error.response?.data?.message || 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.');
      }
      return rejectWithValue(error.response?.data?.message || error.userMessage || error.message || 'Yorumlar yüklenemedi');
    }
  },
  {
    condition: ({ force } = {}, { getState }) => {
      if (force) return true;
      const lastFetched = getState().dashboard.lastFetched.ratings;
      if (shouldSkipFetch(lastFetched, STALE_TIMES.ratings)) {
        return false;
      }
      return true;
    },
  }
);

const initialState = {
  // Dashboard stats (WebSocket ve diğer veriler için)
  dashboardStats: null,
  
  // Real-time veriler
  realtimeData: null,
  realtimeLoading: false,
  realtimeError: null,
  
  // Kampanya analizleri
  campaignAnalytics: DEFAULT_CAMPAIGN_ANALYTICS,
  campaignAnalyticsLoading: false,
  campaignSectionsLoading: false,
  campaignSectionsSettled: false,
  campaignAnalyticsError: null,
  
  // Kullanıcı davranışı
  userBehavior: null,
  userBehaviorLoading: false,
  userBehaviorError: null,
  
  // Son aktiviteler (WebSocket'ten gelir)
  recentActivities: [],

  // Yorumlar (ratings)
  ratings: DEFAULT_RATINGS.data,
  ratingsPagination: DEFAULT_RATINGS.pagination,
  ratingsLoading: false,
  ratingsError: null,
  
  // Platform dağılımı (iOS/Android) — unified KPI / dashboard:sync
  platformDistribution: null,

  appVersionDistribution: null,
  
  // Kampanya CTR analizi
  campaignCTR: null,
  campaignCTRLoading: false,
  campaignCTRError: null,
  
  // Kullanıcı istatistikleri (unified KPI / dashboard:sync)
  totalUsers: 0,
  newUsers: 0,
  screenViews: 0,
  
  // Genel durum
  loading: false,
  error: null,
  lastFetched: {
    realtimeData: null,
    campaignAnalytics: null,
    kpiCore: null,
    kpiCampaigns: null,
    userBehavior: null,
    platformDistribution: null,
    appVersionDistribution: null,
    campaignCTR: null, // ✅ Kampanya CTR analizi için
    ratings: null, // ✅ Yorumlar için
  },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardData: (state) => {
      state.dashboardStats = null;
      state.realtimeData = null;
      state.campaignAnalytics = DEFAULT_CAMPAIGN_ANALYTICS;
      state.campaignSectionsLoading = false;
      state.campaignSectionsSettled = false;
      state.userBehavior = null;
      state.recentActivities = [];
      state.totalUsers = 0;
      state.newUsers = 0;
      state.screenViews = 0;
      state.realtimeError = null;
      state.campaignAnalyticsError = null;
      state.userBehaviorError = null;
      state.lastFetched = {
        realtimeData: null,
        campaignAnalytics: null,
        kpiCore: null,
        kpiCampaigns: null,
        userBehavior: null,
        platformDistribution: null,
        appVersionDistribution: null,
        campaignCTR: null,
        ratings: null,
      };
    },
    // ✅ Anlık aktif kullanıcı: backend'den 'dashboard:active-users' event'iyle gelen
    // MUTLAK değer. Delta biriktirme yerine otoriter sayı — kayma yapmaz.
    setActiveUsersNow: (state, action) => {
      const count = action.payload;
      if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) return;
      if (!state.realtimeData) {
        state.realtimeData = {};
      }
      state.realtimeData.activeUsers = count;
      state.lastFetched.realtimeData = Date.now();
    },
    // ✅ KPI güncelleme: kpiDelta değerlerini mevcut state'e ekler (anlık güncelleme)
    // analytics:event event'inden gelir
    updateKPI: (state, action) => {
      // Hem kpiDelta hem de event geçirilebilir (campaignTitle için)
      const payload = action.payload;
      const kpiDelta = payload.kpiDelta || payload; // Geriye dönük uyumluluk için
      const event = payload.event;
      
      if (!kpiDelta) return;
      
      // dashboardStats.kpi'yi güncelle
        if (!state.dashboardStats) {
          state.dashboardStats = {};
        }
        if (!state.dashboardStats.kpi) {
          state.dashboardStats.kpi = {};
        }
      
      // Delta değerlerini mevcut değerlere ekle
      if (kpiDelta.totalEvents !== undefined) {
        const eskiTotalEvents = state.dashboardStats.kpi.totalEvents ?? state.campaignAnalytics?.totalEvents ?? 0;
        state.dashboardStats.kpi.totalEvents = eskiTotalEvents + kpiDelta.totalEvents;
        if (state.campaignAnalytics) {
          state.campaignAnalytics.totalEvents = state.dashboardStats.kpi.totalEvents;
        }
      }
      
      // ✅ YENİ: totalUsers artık delta olarak gelmiyor (her zaman 0)
      // totalUsers değeri sadece dashboard:sync event'inden gelen toplam değer kullanılmalı
      // kpiDelta.totalUsers görmezden gelinmeli
      if (kpiDelta.totalUsers !== undefined && kpiDelta.totalUsers !== 0) {
      }
      
      // ✅ YENİ: newUsers sadece user_registered event'inde artıyor
      // Sadece pozitif değerler için güncelleme yap
      if (kpiDelta.newUsers !== undefined && kpiDelta.newUsers > 0) {
        const eskiNewUsers = state.dashboardStats.kpi.newUsers ?? state.newUsers ?? 0;
        state.dashboardStats.kpi.newUsers = eskiNewUsers + kpiDelta.newUsers;
        state.newUsers = state.dashboardStats.kpi.newUsers;
        state.dashboardStats.newUsers = state.newUsers;
      }
      
      if (kpiDelta.sessions !== undefined) {
        const eskiSessions = state.dashboardStats.kpi.sessions ?? state.dashboardStats.sessions ?? 0;
        state.dashboardStats.kpi.sessions = eskiSessions + kpiDelta.sessions;
        state.dashboardStats.sessions = state.dashboardStats.kpi.sessions;
      }
      
      if (kpiDelta.screenViews !== undefined) {
        const eskiScreenViews = state.dashboardStats.kpi.screenViews ?? state.screenViews ?? 0;
        state.dashboardStats.kpi.screenViews = eskiScreenViews + kpiDelta.screenViews;
        state.screenViews = state.dashboardStats.kpi.screenViews;
      }
      
      // ✅ Aktif Kullanıcı (24 saat) delta - Login olunca güncellenir
      if (kpiDelta.activeUsers !== undefined) {
        const eskiActiveUsers = state.dashboardStats.kpi.activeUsers ?? state.campaignAnalytics?.activeUsers ?? 0;
        state.dashboardStats.kpi.activeUsers = eskiActiveUsers + kpiDelta.activeUsers;
        if (state.campaignAnalytics) {
          state.campaignAnalytics.activeUsers = state.dashboardStats.kpi.activeUsers;
        }
      }
      
      // ✅ activeUsersNow delta'sı kaldırıldı: anlık aktif sayısı artık backend'den
      // 'dashboard:active-users' event'iyle MUTLAK değer olarak gelir (setActiveUsersNow).
      // Delta birikimi kayma yaratıyordu (kaçan WS mesajı, session_end gelmeyen crash'ler).


      if (kpiDelta.campaignViews !== undefined && kpiDelta.campaignViews > 0) {
        }
      
      // ✅ Toplam kampanya sayısı delta
      if (kpiDelta.totalCampaigns !== undefined && kpiDelta.totalCampaigns !== 0) {
        const eskiTotalCampaigns = state.dashboardStats.kpi.totalCampaigns ?? state.campaignAnalytics?.totalCampaigns ?? 0;
        state.dashboardStats.kpi.totalCampaigns = eskiTotalCampaigns + kpiDelta.totalCampaigns;
        if (state.campaignAnalytics) {
          state.campaignAnalytics.totalCampaigns = state.dashboardStats.kpi.totalCampaigns;
        }
      }
      
      // ✅ Popular Campaigns Delta - Belirli bir kampanyanın clicks'ini artır
      if (kpiDelta.popularCampaignsDelta) {
        const delta = kpiDelta.popularCampaignsDelta;
        const campaignId = delta.campaignId;
        const clicksDelta = delta.clicks || 1;
        
        if (campaignId && state.campaignAnalytics) {
          if (!Array.isArray(state.campaignAnalytics.popularCampaigns)) {
            state.campaignAnalytics.popularCampaigns = [];
          }
          
          // Mevcut kampanyayı bul veya yeni ekle
          const existingCampaignIndex = state.campaignAnalytics.popularCampaigns.findIndex(
            c => c.campaignId === campaignId
          );
          
          if (existingCampaignIndex >= 0) {
            // Mevcut kampanyayı güncelle (sadece clicks +1, impressions değişmez)
            const existingCampaign = state.campaignAnalytics.popularCampaigns[existingCampaignIndex];
            const newClicks = (existingCampaign.clicks || 0) + clicksDelta;
            const impressions = existingCampaign.impressions || 0;
            const newCtr = impressions > 0 ? ((newClicks / impressions) * 100) : 0;
            
            // ✅ Kampanya adını güncelle (eğer yoksa event'ten al)
            const campaignTitle = existingCampaign.campaignTitle 
              || existingCampaign.campaignName
              || delta.campaignTitle 
              || (event?.data?.campaignTitle) 
              || (event?.data?.campaignName)
              || null;
            
            state.campaignAnalytics.popularCampaigns[existingCampaignIndex] = {
              ...existingCampaign,
              campaignTitle: campaignTitle || existingCampaign.campaignTitle, // Mevcut varsa koru
              campaignName: campaignTitle || existingCampaign.campaignName, // Geriye dönük uyumluluk
              clicks: newClicks,
              ctr: newCtr,
            };
          } else {
            // Yeni kampanya ekle (impressions 0, sadece clicks var)
            // Kampanya adını önce popularCampaignsDelta'den, sonra event'ten al
            const campaignTitle = delta.campaignTitle 
              || (event?.data?.campaignTitle) 
              || (event?.data?.campaignName)
              || null; // campaignId kullanmayalım, dashboard:sync geldiğinde backend'den gelecek
            
            state.campaignAnalytics.popularCampaigns.push({
              campaignId: campaignId,
              campaignTitle: campaignTitle, // ✅ Kampanya adı (ID değil)
              campaignName: campaignTitle, // ✅ Geriye dönük uyumluluk için
              impressions: 0,
              clicks: clicksDelta,
              ctr: 0,
              isPersonalized: delta.isPersonalized ?? false,
            });
          }
          
          // En çok tıklanan 5 kampanyayı tut (clicks'a göre sırala)
          state.campaignAnalytics.popularCampaigns.sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
          if (state.campaignAnalytics.popularCampaigns.length > 5) {
            state.campaignAnalytics.popularCampaigns = state.campaignAnalytics.popularCampaigns.slice(0, 5);
          }
          
          // ✅ campaignCTR.total.detailViews'ı güncelle (kampanya etkileşimi için)
          // Kampanya detayı açıldığında (click olduğunda) detailViews artmalı
          if (state.campaignCTR) {
            const isPersonalized = delta.isPersonalized ?? false;
            const currentGeneralDetailViews = state.campaignCTR.general?.detailViews || 0;
            const currentPersonalizedDetailViews = state.campaignCTR.personalized?.detailViews || 0;
            
            if (!isPersonalized) {
              // Genel kampanya tıklaması
              const newGeneralDetailViews = currentGeneralDetailViews + clicksDelta;
              const generalImpressions = state.campaignCTR.general?.impressions || 0;
              const newGeneralCtr = generalImpressions > 0 ? ((newGeneralDetailViews / generalImpressions) * 100) : 0;
              
              state.campaignCTR.general = {
                ...state.campaignCTR.general,
                detailViews: newGeneralDetailViews,
                ctr: newGeneralCtr,
              };
            } else {
              // Kişisel kampanya tıklaması
              const newPersonalizedDetailViews = currentPersonalizedDetailViews + clicksDelta;
              const personalizedImpressions = state.campaignCTR.personalized?.impressions || 0;
              const newPersonalizedCtr = personalizedImpressions > 0 ? ((newPersonalizedDetailViews / personalizedImpressions) * 100) : 0;
              
              state.campaignCTR.personalized = {
                ...state.campaignCTR.personalized,
                detailViews: newPersonalizedDetailViews,
                ctr: newPersonalizedCtr,
              };
            }
            
            // Total'ı güncelle
            const newTotalDetailViews = (state.campaignCTR.general?.detailViews || 0) + (state.campaignCTR.personalized?.detailViews || 0);
            const newTotalImpressions = (state.campaignCTR.general?.impressions || 0) + (state.campaignCTR.personalized?.impressions || 0);
            const newTotalCtr = newTotalImpressions > 0 ? ((newTotalDetailViews / newTotalImpressions) * 100) : 0;
            
            state.campaignCTR.total = {
              ...state.campaignCTR.total,
              detailViews: newTotalDetailViews,
              impressions: newTotalImpressions,
              ctr: newTotalCtr,
            };
          }
          
        }
      }
      
      // ✅ Popular Campaigns Impressions Delta - Impressions'ı güncelle
      if (kpiDelta.popularCampaignsImpressionsDelta) {
        const impressionsDelta = kpiDelta.popularCampaignsImpressionsDelta;
        
        if (state.campaignAnalytics && Array.isArray(state.campaignAnalytics.popularCampaigns)) {
          // isPersonalized'e göre impressions güncelle
          state.campaignAnalytics.popularCampaigns = state.campaignAnalytics.popularCampaigns.map(campaign => {
            const isPersonalized = campaign.isPersonalized ?? false;
            
            // Genel kampanyalar için ana sayfa görüntülenmeleri
            if (!isPersonalized && impressionsDelta.isHomeScreen) {
              const newImpressions = (campaign.impressions || 0) + 1;
              const clicks = campaign.clicks || 0;
              const newCtr = newImpressions > 0 ? ((clicks / newImpressions) * 100) : 0;
              
              return {
                ...campaign,
                impressions: newImpressions,
                ctr: newCtr,
              };
            }
            
            // Kişisel kampanyalar için kozakartım sayfası görüntülenmeleri
            if (isPersonalized && impressionsDelta.isKozakartimScreen) {
              const newImpressions = (campaign.impressions || 0) + 1;
              const clicks = campaign.clicks || 0;
              const newCtr = newImpressions > 0 ? ((clicks / newImpressions) * 100) : 0;
              
              return {
                ...campaign,
                impressions: newImpressions,
                ctr: newCtr,
              };
            }
            
            return campaign;
          });
          
        }
        
        // ✅ campaignCTR state'ini de güncelle (Genel ve Kişiselleştirilmiş tabloları için)
        if (state.campaignCTR) {
          let generalUpdated = false;
          let personalizedUpdated = false;
          
          // Genel kampanyalar için ana sayfa görüntülenmeleri
          if (impressionsDelta.isHomeScreen) {
            const currentGeneralImpressions = state.campaignCTR.general?.impressions || 0;
            const currentGeneralDetailViews = state.campaignCTR.general?.detailViews || 0;
            const newGeneralImpressions = currentGeneralImpressions + 1;
            const newGeneralCtr = newGeneralImpressions > 0 ? ((currentGeneralDetailViews / newGeneralImpressions) * 100) : 0;
            
            state.campaignCTR.general = {
              ...state.campaignCTR.general,
              impressions: newGeneralImpressions,
              ctr: newGeneralCtr,
            };
            
            generalUpdated = true;
          }
          
          // Kişisel kampanyalar için kozakartım sayfası görüntülenmeleri
          if (impressionsDelta.isKozakartimScreen) {
            const currentPersonalizedImpressions = state.campaignCTR.personalized?.impressions || 0;
            const currentPersonalizedDetailViews = state.campaignCTR.personalized?.detailViews || 0;
            const newPersonalizedImpressions = currentPersonalizedImpressions + 1;
            const newPersonalizedCtr = newPersonalizedImpressions > 0 ? ((currentPersonalizedDetailViews / newPersonalizedImpressions) * 100) : 0;
            
            state.campaignCTR.personalized = {
              ...state.campaignCTR.personalized,
              impressions: newPersonalizedImpressions,
              ctr: newPersonalizedCtr,
            };
            
            personalizedUpdated = true;
          }
          
          // Total'ı güncelle (sadece bir kez, eğer general veya personalized güncellendiyse)
          if (generalUpdated || personalizedUpdated) {
            const newTotalImpressions = (state.campaignCTR.general?.impressions || 0) + (state.campaignCTR.personalized?.impressions || 0);
            const newTotalDetailViews = (state.campaignCTR.general?.detailViews || 0) + (state.campaignCTR.personalized?.detailViews || 0);
            const newTotalCtr = newTotalImpressions > 0 ? ((newTotalDetailViews / newTotalImpressions) * 100) : 0;
            
            state.campaignCTR.total = {
              ...state.campaignCTR.total,
              impressions: newTotalImpressions,
              detailViews: newTotalDetailViews,
              ctr: newTotalCtr,
            };
          }
        } else {
          // campaignCTR yoksa başlat
          const generalImpressions = impressionsDelta.isHomeScreen ? 1 : 0;
          const personalizedImpressions = impressionsDelta.isKozakartimScreen ? 1 : 0;
          
          state.campaignCTR = {
            general: { impressions: generalImpressions, detailViews: 0, ctr: 0 },
            personalized: { impressions: personalizedImpressions, detailViews: 0, ctr: 0 },
            total: { impressions: generalImpressions + personalizedImpressions, detailViews: 0, ctr: 0 },
          };
        }
      }
      
      state.lastFetched.dashboardStats = Date.now();
    },
    
    // ✅ Recent activity ekle: analytics:event event'inden gelir
    addRecentActivity: (state, action) => {
      const event = action.payload;
      
      if (!event) return;
      if (shouldExcludeFromActivityFeed(event.eventType, normalizeActivityData(event))) return;
      const backendId = event.id || event.activityId;

      const activity = {
        id: backendId || `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        user: event.userId || 'anonymous',
        action: event.eventType,
        eventCategory: event.eventCategory,
        data: normalizeActivityData(event),
        time: 'Az önce',
        timestamp: new Date(event.timestamp || new Date()).getTime()
      };
      
      if (!state.recentActivities) {
        state.recentActivities = [];
      }

      // Yalnızca backend id varsa mükerrer engelle
      if (backendId && state.recentActivities.some((a) => a.id === backendId)) {
        return;
      }
      
      state.recentActivities.unshift(activity);
      
      // Feed ile uyumlu: son 50 anlık aktivite
      if (state.recentActivities.length > 50) {
        state.recentActivities = state.recentActivities.slice(0, 50);
      }
      
      // ✅ NOT: campaign_detail_view event'i için popularCampaigns güncellemesi
      // updateKPI reducer'ında popularCampaignsDelta ile yapılıyor.
      // Burada tekrar güncelleme yapmıyoruz çünkü clicks 2 kez artıyordu.
      
    },
    
    // ✅ Dashboard sync: dashboard:sync (WS) veya KPI fetch sonrası
    syncDashboard: (state, action) => {
      const rawPayload = action.payload;
      if (!rawPayload) return;

      const hasSyncWrapper = rawPayload && typeof rawPayload === 'object' && 'syncData' in rawPayload;
      const syncData = hasSyncWrapper ? rawPayload.syncData : rawPayload;
      // KPI fetch / dashboard:sync — varsayılan olarak activeUsersNow güncellemez
      const updateActiveUsersNow = hasSyncWrapper
        ? rawPayload.updateActiveUsersNow === true
        : false;

      if (!syncData) return;

      const syncScope = hasSyncWrapper ? (rawPayload.syncScope || 'all') : 'all';
      const includesCore = syncScope === 'core' || syncScope === 'all';
      const includesCampaigns = syncScope === 'campaigns' || syncScope === 'all';
      const parsedKpi = parseKpiPayload(syncData.kpi);

      // —— Core KPI (kartlar, oturum, kullanıcı sayıları) ——
      if (parsedKpi && includesCore) {
        const { kpiWithoutExtras } = parsedKpi;

        if (!state.dashboardStats) {
          state.dashboardStats = {};
        }
        if (!state.dashboardStats.kpi) {
          state.dashboardStats.kpi = {};
        }

        state.dashboardStats.kpi = {
          ...(state.dashboardStats.kpi || {}),
          ...kpiWithoutExtras,
        };

        if (syncData.kpi.sessions !== undefined) {
          state.dashboardStats.sessions = syncData.kpi.sessions;
        }
        if (kpiWithoutExtras.totalUsers !== undefined && kpiWithoutExtras.totalUsers !== null) {
          state.dashboardStats.kpi.totalUsers = kpiWithoutExtras.totalUsers;
          state.totalUsers = kpiWithoutExtras.totalUsers;
        }
        if (kpiWithoutExtras.newUsers !== undefined && kpiWithoutExtras.newUsers !== null) {
          state.dashboardStats.newUsers = kpiWithoutExtras.newUsers;
          state.newUsers = kpiWithoutExtras.newUsers;
        }
        if (kpiWithoutExtras.sessions !== undefined && kpiWithoutExtras.sessions !== null) {
          state.dashboardStats.kpi.sessions = kpiWithoutExtras.sessions;
          state.dashboardStats.sessions = kpiWithoutExtras.sessions;
        }

        if (!state.campaignAnalytics) {
          state.campaignAnalytics = DEFAULT_CAMPAIGN_ANALYTICS;
        }

        state.lastFetched.campaignAnalytics = Date.now();
        state.lastFetched.dashboardStats = Date.now();
      }

      // —— Kampanya KPI (popularCampaigns, screenViews, campaignViews) ——
      if (includesCampaigns) {
        if (parsedKpi) {
          applyCampaignKpiFields(state, parsedKpi.kpiWithoutExtras, parsedKpi.campaignViews);
        }
        mergePopularCampaignsIntoState(
          state,
          syncData,
          parsedKpi?.kpiWithoutExtras
        );
        state.lastFetched.campaignAnalytics = Date.now();
      }
      
      // activeUsersNow: yalnızca dashboard:active-users → setActiveUsersNow (sync/KPI'dan okunmaz)
      if (
        updateActiveUsersNow === true
        && syncData.activeUsersNow !== undefined
        && syncData.activeUsersNow !== null
      ) {
        if (!state.realtimeData) {
          state.realtimeData = {};
        }
        state.realtimeData.activeUsers = syncData.activeUsersNow;
        state.lastFetched.realtimeData = Date.now();
      }

      // activeUsersInRange: seçili dönemde tekil aktif kullanıcı (tarih filtresine bağlı)
      if (
        includesCore
        && syncData.activeUsersInRange !== undefined
        && syncData.activeUsersInRange !== null
      ) {
        if (!state.dashboardStats) {
          state.dashboardStats = {};
        }
        if (!state.dashboardStats.kpi) {
          state.dashboardStats.kpi = {};
        }
        state.dashboardStats.kpi.activeUsers = syncData.activeUsersInRange;
        if (state.campaignAnalytics) {
          state.campaignAnalytics.activeUsers = syncData.activeUsersInRange;
        }
      }

      // Chart verileri — KPI unified ham seriler (userGrowth = büyüme, userTrend = ayrı metrik)
      if (includesCore && syncData.charts) {
        if (!state.dashboardStats) {
          state.dashboardStats = {};
        }
        if (!state.dashboardStats.charts) {
          state.dashboardStats.charts = {};
        }

        state.dashboardStats.charts.userGrowth = Array.isArray(syncData.charts.userGrowth)
          ? syncData.charts.userGrowth
          : [];
        state.dashboardStats.charts.userTrend = Array.isArray(syncData.charts.userTrend)
          ? syncData.charts.userTrend
          : [];
        state.lastFetched.userTrend = Date.now();
      }
        
      // Recent activities varsa güncelle (üzerine yazma — WS ile gelenleri koru)
      if (includesCore && syncData.recentActivities) {
        let activities = syncData.recentActivities;
        
        if (activities.success && activities.data) {
          activities = activities.data;
        }
        
        const incoming = Array.isArray(activities)
          ? activities.filter((row) => !activityRowShouldExcludeFromFeed(row))
          : [];

        const existing = state.recentActivities || [];
        const seen = new Set(existing.map((row) => activityRowFingerprint(row)));
        const merged = [...existing];

        incoming.forEach((row) => {
          const fp = activityRowFingerprint(row);
          if (seen.has(fp)) return;
          seen.add(fp);
          merged.push(row);
        });

        merged.sort((a, b) => {
          const ta = new Date(a.timestamp || a.time || 0).getTime();
          const tb = new Date(b.timestamp || b.time || 0).getTime();
          return tb - ta;
        });

        state.recentActivities = merged.slice(0, 50);
      }
      
      // User behavior varsa güncelle
      if (includesCore && syncData.userBehavior) {
        state.userBehavior = {
          ...state.userBehavior,
          ...syncData.userBehavior,
        };
        state.lastFetched.userBehavior = Date.now();
      }
      
      // ✅ Platform Distribution — KPI unified / WebSocket
      if (includesCore && syncData.platformDistribution) {
        state.platformDistribution = syncData.platformDistribution;
        state.lastFetched.platformDistribution = Date.now();
      }

      // ✅ Versiyon dağılımı — KPI unified (sp_YKSadakatKullaniciDashboard)
      const versionPayload =
        syncData.appVersionDistribution
        || syncData.kpi?.reportDashboard?.appVersionDistribution;

      if (includesCore && versionPayload) {
        let versionData = versionPayload;
        if (versionPayload.success && versionPayload.data) {
          versionData = versionPayload.data;
        }

        const fc = syncData.filterContext || {};
        const storedDateRange =
          fc.dateRange != null && fc.dateRange !== ''
            ? fc.dateRange
            : (!fc.dateFrom ? 'all' : state.appVersionDistribution?.dateRange || 'all');

        state.appVersionDistribution = {
          versions: Array.isArray(versionData.versions) ? versionData.versions : [],
          unknown: versionData.unknown || 0,
          total: versionData.total || 0,
          source: versionData.source || 'rapor:sp_YKSadakatKullaniciDashboard',
          dateRange: storedDateRange,
        };
        state.lastFetched.appVersionDistribution = Date.now();
      }

      // ✅ Aktivite kohort özeti (yaş/cinsiyet) — KPI unified
      if (includesCore && syncData.activitiesStats) {
        if (!state.dashboardStats) {
          state.dashboardStats = {};
        }
        state.dashboardStats.activitiesStats = syncData.activitiesStats;
      }

      // ✅ Campaign CTR varsa güncelle (WebSocket'ten gelirse)
      if (syncData.campaignCTR) {
        state.campaignCTR = syncData.campaignCTR;
        state.lastFetched.campaignCTR = Date.now();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Campaign Analytics / KPI Data
      .addCase(fetchCampaignAnalytics.pending, (state, action) => {
        const sections = action.meta.arg?.sections;
        if (sections === 'campaigns') {
          state.campaignSectionsLoading = true;
          state.campaignSectionsSettled = false;
        } else {
          state.campaignAnalyticsLoading = true;
        }
        state.campaignAnalyticsError = null;
      })
      .addCase(fetchCampaignAnalytics.fulfilled, (state, action) => {
        const sections = action.meta.arg?.sections;
        const syncScope = action.payload.syncScope || 'all';

        if (sections === 'campaigns') {
          state.campaignSectionsLoading = false;
          state.campaignSectionsSettled = true;
        } else {
          state.campaignAnalyticsLoading = false;
          state.lastFetched.kpiCore = Date.now();
        }

        dashboardSlice.caseReducers.syncDashboard(state, {
          payload: {
            syncData: action.payload.syncData,
            updateActiveUsersNow: action.payload.updateActiveUsersNow === true,
            syncScope,
          },
          type: 'dashboard/syncDashboard',
        });

        if (sections === 'campaigns') {
          const mergedCount = state.campaignAnalytics?.popularCampaigns?.length ?? 0;
          if (mergedCount > 0) {
            state.lastFetched.kpiCampaigns = Date.now();
          }
        }

        state.campaignAnalyticsError = null;
        state.lastFetched.campaignAnalytics = Date.now();
      })
      .addCase(fetchCampaignAnalytics.rejected, (state, action) => {
        const sections = action.meta.arg?.sections;
        if (action.meta?.condition) {
          return;
        }
        if (sections === 'campaigns') {
          state.campaignSectionsLoading = false;
          state.campaignSectionsSettled = true;
        } else {
          state.campaignAnalyticsLoading = false;
        }
        state.campaignAnalyticsError = action.payload;
        state.campaignAnalytics = state.campaignAnalytics || DEFAULT_CAMPAIGN_ANALYTICS;
      })

      // Campaign CTR Analysis
      .addCase(fetchCampaignCTR.pending, (state) => {
        state.campaignCTRLoading = true;
        state.campaignCTRError = null;
      })
      .addCase(fetchCampaignCTR.fulfilled, (state, action) => {
        state.campaignCTRLoading = false;
        
        // ✅ 304 Not Modified Response Kontrolü
        // 304 response'larında payload boş gelir, mevcut state'i korumalıyız
        const isPayloadEmpty = !action.payload || 
          (!action.payload.general && !action.payload.personalized && !action.payload.total) ||
          (action.payload.general?.impressions === 0 && 
           action.payload.general?.detailViews === 0 &&
           action.payload.personalized?.impressions === 0 &&
           action.payload.personalized?.detailViews === 0 &&
           action.payload.total?.impressions === 0 &&
           action.payload.total?.detailViews === 0 &&
           state.campaignCTR !== null &&
           (state.campaignCTR.general?.impressions > 0 || state.campaignCTR.personalized?.impressions > 0));
        
        if (isPayloadEmpty) {
          state.campaignCTRError = null;
          state.lastFetched.campaignCTR = Date.now();
          return; // ✅ State'i güncelleme, mevcut değerleri koru
        }
        
        state.campaignCTR = action.payload;
        state.campaignCTRError = null;
        state.lastFetched.campaignCTR = Date.now();
      })
      .addCase(fetchCampaignCTR.rejected, (state, action) => {
        state.campaignCTRLoading = false;
        state.campaignCTR = {
          general: { impressions: 0, detailViews: 0, ctr: 0 },
          personalized: { impressions: 0, detailViews: 0, ctr: 0 },
          total: { impressions: 0, detailViews: 0, ctr: 0 },
        };
        state.campaignCTRError = action.payload;
        state.lastFetched.campaignCTR = Date.now();
      })

      // Ratings (Dashboard yorumları)
      .addCase(fetchRatings.pending, (state) => {
        state.ratingsLoading = true;
        state.ratingsError = null;
      })
      .addCase(fetchRatings.fulfilled, (state, action) => {
        state.ratingsLoading = false;
        state.ratings = action.payload.data || [];
        state.ratingsPagination = action.payload.pagination || {};
        state.ratingsError = null;
        state.lastFetched.ratings = Date.now();
      })
      .addCase(fetchRatings.rejected, (state, action) => {
        state.ratingsLoading = false;
        state.ratingsError = action.payload;
        state.lastFetched.ratings = Date.now();
      });
  },
});

export const {
  clearDashboardData,
  syncDashboard,
  updateKPI,
  addRecentActivity,
  setActiveUsersNow
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
